import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { useMediaQuery } from 'react-responsive';

import { prependAccountSlug } from '../../../../../../util/account';
import AlbumPhotoGroupIcon from '../../../../../../components/icons/AlbumPhotoGroupIcon';
import AlbumsIcon from '../../../../../../components/icons/AlbumsIcon';
import FolderIcon from '../../../../../../components/icons/FolderIcon';
import { AlbumFolderHierarchyBreadcrumb } from '../../../../../../graphql/queries/album-folder';
import { AlbumHierarchyBreadcrumb } from '../../../../../../graphql/fragments/album';
import Spinner from '../../../../../../components/global/Spinner';
import { getWebGalleryEntityPath } from '../../../../utilities/web-galleries';
import DropdownMenu from '../../../../../../components/global/DropdownMenu';
import NavigationBreadcrumbs from '../../../../../../components/global/NavigationBreadcrumbs';
import Angle from '../../../../../../components/global/Angle';
import DotTrigger from '../../../../../../components/global/DotTrigger';
import AlbumFolderGalleryIcon from '../../../../../../components/icons/AlbumFolderGalleryIcon';
import { hasNavigationalAccess } from '../../helpers';
import { breakpointDown } from '../../../../../../services/responsive/responsive';

const propTypes = {
  className: PropTypes.string,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    album_folder: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  albumPhotoGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  loading: PropTypes.bool,
  albumFolderHierarchy: PropTypes.shape({
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    ).isRequired,
  }),
  mobileSideNav: PropTypes.node,
};

const WebGalleryTitleBreadcrumb = ({
  album,
  albumFolder,
  albumPhotoGroup,
  className,
  loading,
  albumFolderHierarchy,
  mobileSideNav,
}) => {
  const isMobile = useMediaQuery(breakpointDown('md'));
  if (loading) {
    return <Spinner />;
  }

  const entity = albumPhotoGroup || album || albumFolder;
  const isIndividualAlbum = !!album?.root_album_folder?.individual_albums;
  const account =
    albumPhotoGroup?.album?.creator || album?.creator || albumFolder?.account;

  const trail = isIndividualAlbum
    ? [albumPhotoGroup ? album : null]
        .filter(Boolean)
        .map(item => ({
          label: item.name,
          path: prependAccountSlug(getWebGalleryEntityPath(item), account),
        }))
        .reverse()
    : [albumPhotoGroup ? album : null, ...(albumFolderHierarchy?.records ?? [])]
        .filter(Boolean)
        .map(item => ({
          label: item.name,
          path: prependAccountSlug(getWebGalleryEntityPath(item), account),
        }))
        .reverse();

  const completeTrail = [...trail, { label: '' }];

  const classes = classNames({
    'wg-gallery-title-breadcrumb': true,
    'mb-2 mb-lg-0': albumFolder && !albumFolder?.parent,
    [className]: !!className,
  });

  const icon = (() => {
    if (albumPhotoGroup) {
      return <AlbumPhotoGroupIcon color="#1a2e34" />;
    }

    if (album) {
      return <AlbumsIcon color="#1a2e34" />;
    }

    if (albumFolder?.parent) {
      return <FolderIcon color="#1a2e34" />;
    }

    return <AlbumFolderGalleryIcon color="#1a2e34" />;
  })();

  if (!hasNavigationalAccess(account)) {
    return (
      <div className={classes}>
        {albumPhotoGroup && (
          <NavigationBreadcrumbs
            trail={[
              {
                label: album.name,
                path: prependAccountSlug(
                  getWebGalleryEntityPath(album),
                  account,
                ),
              },
            ]}
            maxCrumbs={1}
            mobileMaxCrumbs={1}
          />
        )}

        {(!albumFolder || !!albumFolder?.parent) && (
          <div className="web-gallery-nav-title">
            {icon}
            <h2 className="mb-0">{entity.name}</h2>
          </div>
        )}
        {mobileSideNav && (
          <div className="sidebar-trigger d-lg-none">{mobileSideNav}</div>
        )}
      </div>
    );
  }

  return (
    <div className={classes}>
      {trail.length > 0 && (
        <NavigationBreadcrumbs
          trail={completeTrail}
          maxCrumbs={4}
          breakpoint="md"
          mobileMaxCrumbs={1}
          renderEllipsis={({ skipped }) => (
            <div className="dropdown-menu-dark m-0 mr-2">
              {isMobile ? (
                <Link to={trail[trail.length - 1].path}>
                  <Angle direction="left" className="text-dark text-lg" />
                </Link>
              ) : (
                <DropdownMenu
                  placement="bottom-end"
                  toggle={
                    <span className="navigation-breadcrumbs-item-label navigation-breadcrumbs-item-label-hellip-btn">
                      <DotTrigger className="hellip-btn" />
                    </span>
                  }
                >
                  {skipped.map((trailItem, index) => (
                    <Link
                      to={trailItem.path}
                      key={index}
                      className={`dropdown-item ${
                        !trailItem.label ? 'd-none' : ''
                      }`}
                    >
                      <span className="w-100 text-truncate">
                        {trailItem.label}
                      </span>
                    </Link>
                  ))}
                </DropdownMenu>
              )}
            </div>
          )}
        />
      )}

      {(!albumFolder || !!albumFolder?.parent) && (
        <div className="web-gallery-nav-title">
          {icon}
          <h2 className="mb-0">{entity.name}</h2>
        </div>
      )}
      {mobileSideNav && (
        <div className="sidebar-trigger d-lg-none">{mobileSideNav}</div>
      )}
    </div>
  );
};

WebGalleryTitleBreadcrumb.propTypes = propTypes;

const withAlbumFolderBreadcrumbData = graphql(AlbumFolderHierarchyBreadcrumb, {
  options: ({ albumFolder }) => {
    return {
      variables: {
        id: albumFolder.id,
      },
    };
  },
  props: ({ data: { albumFolder, loading } }) => ({
    loading,
    albumFolderHierarchy: albumFolder?.album_folder_hierarchy,
  }),
  skip: ({ albumFolder }) => !albumFolder,
});

const withAlbumBreadcrumbData = graphql(AlbumHierarchyBreadcrumb, {
  options: ({ album, albumPhotoGroup }) => {
    return {
      variables: {
        id: albumPhotoGroup?.album?.id ?? album?.id,
      },
    };
  },
  props: ({ data: { album, loading } }) => ({
    albumFolderHierarchy: album?.album_folder_hierarchy,
    loading,
  }),
  skip: ({ album, albumPhotoGroup }) => !album && !albumPhotoGroup,
});

export default compose(
  withAlbumFolderBreadcrumbData,
  withAlbumBreadcrumbData,
)(WebGalleryTitleBreadcrumb);
